import { useState, useEffect, useMemo } from "react";
import { X } from "lucide-react";
import { useConfigStore } from "../../../../store/configStore";
import { useEditorStore } from "../../../../store/editorStore";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MoveSkuDialog } from "./components/MoveSkuDialog";
import { SkinInfoSection } from "./components/SkinInfoSection";
import { SkuPriceConfig } from "./components/SkuPriceConfig";
import { BoundTonings } from "./components/BoundTonings";
import { ToningColorPicker } from "./components/ToningColorPicker";
import { UpdateSkuThumb } from "./components/UpdateSkuThumb";

export function SkuDetailPanel() {
  const { templateConfig, parsedSkins } = useConfigStore();

  const {
    getSkuErrors,
    cleanSkuToning,
    updateSkuPro,
    updateSkuHidden,
    removeSkuFromSelection,
    addSkuToSelection,
    editingSkuId,
    previewSkinName,
    setEditingSku,
    setPreviewSkinName,
    selectedToningColors,
  } = useEditorStore();

  const [proValue, setProValue] = useState("");
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);

  // 当前显示的 skinName
  const currentSkinName =
    previewSkinName ||
    (() => {
      // 从 editingSkuId 反查 skinName（直接从 skus 获取）
      if (!editingSkuId || !templateConfig) return null;
      const sku = templateConfig.skus?.[editingSkuId];
      return sku?.skinName || null;
    })();

  // 从 parsedSkins 获取当前 skin（含 skuId, category）
  const currentSkin = useMemo(() => {
    if (!currentSkinName) return null;
    return parsedSkins.find((s) => s.name === currentSkinName) || null;
  }, [currentSkinName, parsedSkins]);

  // 从 skus 获取 SKU 配置（独立于 Selection）
  const skuConfig = useMemo(() => {
    if (!currentSkin || !templateConfig) return { pro: 0 };
    return (
      templateConfig.skus?.[currentSkin.skuId] || {
        pro: 0,
        skinName: currentSkinName,
      }
    );
  }, [currentSkin, templateConfig, currentSkinName]);

  // 检查是否已添加到某个 Selection
  const addedSelection = useMemo(() => {
    if (!templateConfig) return null;
    const targetSkuId = currentSkin?.skuId || editingSkuId;
    if (!targetSkuId) return null;

    for (const sel of templateConfig.selection) {
      const skuIds = sel.skuIds || [];
      if (skuIds.includes(targetSkuId)) {
        return sel;
      }
    }
    return null;
  }, [currentSkin, templateConfig, editingSkuId]);

  // 当 SKU 配置变化时，更新本地状态
  useEffect(() => {
    setProValue((skuConfig.pro || 0).toString());
  }, [skuConfig.pro]);

  // 关闭面板
  const handleClose = () => {
    setEditingSku(null, null);
    setPreviewSkinName(null);
  };

  // 添加到 Selection
  const handleAddToSelection = (selectionId: string) => {
    if (currentSkinName) {
      addSkuToSelection(selectionId, currentSkinName);
    }
  };

  // 没有内容显示
  if (!currentSkinName) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader className="pb-2 flex-shrink-0">
          <CardTitle className="text-lg">SKU 详情</CardTitle>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center">
          <p className="text-sm text-muted-foreground text-center">
            点击任意 Skin 查看详情
          </p>
        </CardContent>
      </Card>
    );
  }

  const handleProChange = (value: string) => {
    setProValue(value);
    const num = parseInt(value);
    if (!isNaN(num) && num >= 0 && currentSkinName) {
      updateSkuPro(currentSkinName, num);
    }
  };

  const selections = templateConfig?.selection || [];

  // 从 store 获取 SKU 错误（集中校验）
  const skuErrors = currentSkin ? getSkuErrors(currentSkin.skuId) : [];

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="pb-2 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <CardTitle className="text-lg">SKU 详情</CardTitle>
              {addedSelection ? (
                <Badge variant="secondary" className="text-[10px]">
                  {addedSelection.name || addedSelection.id}
                </Badge>
              ) : (
                <Badge variant="destructive" className="text-[10px]">
                  未添加
                </Badge>
              )}
            </div>
            <div className="text-xs font-mono text-muted-foreground mt-0.5">
              ID: {currentSkin?.skuId}
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 shrink-0"
            onClick={handleClose}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="flex-1 overflow-auto space-y-4">
        {/* 如果皮肤已从 Spine 文件中移除，显示提示 */}
        {!currentSkin && (
          <div className="p-3 rounded-lg border bg-destructive/10 border-destructive px-4 py-3">
            <h4 className="text-sm font-semibold text-destructive mb-1">Skin 已失效</h4>
            <p className="text-xs text-destructive/80 mb-2">
              当前 Spine ZIP 中找不到名为 {currentSkinName} 的皮肤。这通常是因为你上传了新的 ZIP 文件并移除了此皮肤。
            </p>
            {addedSelection && (
               <Button 
                variant="destructive" 
                size="sm" 
                className="w-full text-xs h-7"
                onClick={() => {
                  const targetSkuId = editingSkuId;
                  if (targetSkuId) {
                    removeSkuFromSelection(addedSelection.id, targetSkuId);
                  }
                }}
              >
                从【{addedSelection.name || addedSelection.id}】中移除此组件
              </Button>
            )}
          </div>
        )}

        {/* 缩略图 + 自定义封面上传 */}
        <div className="flex justify-center">
          <UpdateSkuThumb
            skuId={currentSkin?.skuId || editingSkuId || ""}
            skinName={currentSkinName}
            thumbAssetId={
              "thumbAssetId" in skuConfig
                ? skuConfig.thumbAssetId
                : undefined
            }
          />
        </div>

        {/* Skin 信息 + Selection 操作 */}
        <SkinInfoSection
          currentSkinName={currentSkinName}
          currentSkin={currentSkin}
          skuConfig={skuConfig}
          addedSelection={addedSelection}
          selections={selections}
          templateConfig={templateConfig}
          onMoveClick={() => setMoveDialogOpen(true)}
          onRemove={() => {
            const targetSkuId = currentSkin?.skuId || editingSkuId;
            if (targetSkuId) {
              removeSkuFromSelection(addedSelection.id, targetSkuId);
            }
          }}
          onAddToSelection={handleAddToSelection}
        />

        {/* 隐藏 + 价格配置 */}
        <SkuPriceConfig
          skuConfig={skuConfig}
          proValue={proValue}
          currentSkinName={currentSkinName}
          onProChange={handleProChange}
          onHiddenChange={(checked) =>
            updateSkuHidden(currentSkinName, checked)
          }
        />

        {/* 已绑定的调色方案 */}
        <BoundTonings
          skuConfig={skuConfig}
          templateConfig={templateConfig}
          currentSkin={currentSkin}
          skuErrors={skuErrors}
          onCleanToning={cleanSkuToning}
        />

        {/* 颜色选择器 */}
        {skuConfig.toningIds && skuConfig.toningIds.length > 0 && (
          <ToningColorPicker
            toningIds={skuConfig.toningIds}
            templateConfig={templateConfig}
            selectedToningColors={selectedToningColors}
            onColorSelect={(toningId, colorId) => {
              const { clearToningColor, setToningColor, selectedToningColors } =
                useEditorStore.getState();
              // Toggle 逻辑：如果已选中则取消
              if (selectedToningColors[toningId] === colorId) {
                clearToningColor(toningId);
                // 如果当前预览的是这个，需要清除预览状态
                if (useEditorStore.getState().previewToningId === toningId) {
                  useEditorStore.getState().setPreviewToningId(null);
                }
              } else {
                // 选择新颜色时，先清除该 SKU 绑定的其他 toning 的颜色
                // 避免"需要手动取消 A 才能让 B 生效"的问题
                const otherToningIds = (skuConfig.toningIds || []).filter(
                  (id: string) => id !== toningId,
                );
                for (const otherId of otherToningIds) {
                  if (selectedToningColors[otherId]) {
                    clearToningColor(otherId);
                  }
                }
                // 然后选中新的
                setToningColor(toningId, colorId);
                useEditorStore.getState().setPreviewToningId(toningId);
              }
            }}
          />
        )}
      </CardContent>

      {/* 移动 SKU 弹窗 */}
      {addedSelection && currentSkin && (
        <MoveSkuDialog
          open={moveDialogOpen}
          onOpenChange={setMoveDialogOpen}
          sku={{
            id: currentSkin.skuId,
            skinName: currentSkinName,
            ...skuConfig,
          }}
          fromSelectionId={addedSelection.id}
        />
      )}
    </Card>
  );
}
